import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import "./LoveListPage.css";
import "./LovePaid.css";

import Navbar from "./Navbar";
import BannerAds from "./BannerAds";
import LovePaidCard from "./LovePaidCard";
import LoveAvatar from "./LoveAvatar";

const BASE_URL =
  window.location.hostname === "localhost"
    ? "http://localhost:5000"
    : "https://success-blog-app-backend.onrender.com";

const LOVE_URL = `${BASE_URL}/api/love`;
const PAID_URL = `${BASE_URL}/api/love/paid`;

const DAY_MS = 24 * 60 * 60 * 1000;
const REFRESH_MS = 60 * 1000;

const api = axios.create({
  timeout: 12000,
});

const RANK_META = [
  {
    icon: "👑",
    label: "1st",
    color: "#fbbf24",
  },
  {
    icon: "🥈",
    label: "2nd",
    color: "#94a3b8",
  },
  {
    icon: "🥉",
    label: "3rd",
    color: "#fb923c",
  },
];

/* =========================================================
   HELPERS
========================================================= */

function getExpiryFromPremiumAt(premiumAt) {
  if (!premiumAt) return 0;

  const start = new Date(premiumAt).getTime();

  if (!Number.isFinite(start)) return 0;

  return start + DAY_MS;
}

function getRemaining(diff) {
  if (!Number.isFinite(diff) || diff <= 0) {
    return {
      text: "Expired",
      diff: 0,
      pct: 0,
    };
  }

  const hours = Math.floor(diff / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);

  return {
    text: `${hours}h ${minutes}m ${seconds}s`,
    diff,
    pct: Math.max(
      0,
      Math.min(100, Math.round((diff / DAY_MS) * 100))
    ),
  };
}

/* =========================================================
   TIMER
   IMPORTANT:
   Only timer component re-renders every second.
   Whole page will NOT re-render.
========================================================= */

const TimerBar = memo(function TimerBar({ expiresAt }) {
  const [diff, setDiff] = useState(() =>
    Math.max(0, expiresAt - Date.now())
  );

  useEffect(() => {
    let mounted = true;

    const update = () => {
      if (!mounted) return;

      const next = Math.max(0, expiresAt - Date.now());

      setDiff((previous) => {
        if (previous === next) return previous;
        return next;
      });
    };

    update();

    const timer = setInterval(update, 1000);

    return () => {
      mounted = false;
      clearInterval(timer);
    };
  }, [expiresAt]);

  const time = getRemaining(diff);

  const danger = diff > 0 && diff <= 3 * 60 * 60 * 1000;

  return (
    <>
      <div className="ll-timer">
        <span aria-hidden="true">⏱</span>
        <span>{time.text}</span>
      </div>

      <div className="ll-timer-bar-wrap">
        <div
          className={`ll-timer-bar-fill ${
            danger ? "ll-bar-danger" : ""
          }`}
          style={{
            width: `${time.pct}%`,
          }}
        />
      </div>
    </>
  );
});

/* =========================================================
   RANK
========================================================= */

const RankBadge = memo(function RankBadge({ index }) {
  const meta = RANK_META[index];

  if (!meta) {
    return (
      <div className="ll-rank-badge ll-rank-plain">
        #{index + 1}
      </div>
    );
  }

  return (
    <div
      className="ll-rank-badge"
      style={{ color: meta.color }}
    >
      {meta.icon} {meta.label}
    </div>
  );
});

/* =========================================================
   ONE PERSON — name on top, that person's OWN photo underneath.
   Two of these sit side by side on every card, at every screen size, with the
   ❤️ / 🤝 between them. The photo is optional: LoveAvatar falls back to the
   person's initial rather than a broken image.
========================================================= */

const Person = memo(function Person({ name, image }) {
  return (
    <div className="ll-person">
      <span className="ll-name">{name}</span>
      <LoveAvatar src={image} name={name} />
    </div>
  );
});

/* =========================================================
   NORMAL LOVE CARD
========================================================= */

const LoveCard = memo(function LoveCard({ item, index }) {
  const type = item.type === "love" ? "love" : "friendship";

  const expiresAt = useMemo(
    () => getExpiryFromPremiumAt(item.premiumAt),
    [item.premiumAt]
  );

  return (
    <article
      className={[
        "ll-card",
        index === 0 ? "ll-card-gold" : "",
        index === 1 ? "ll-card-silver" : "",
        index === 2 ? "ll-card-bronze" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <RankBadge index={index} />

      <div className="ll-card-heart" aria-hidden="true">
        {type === "love" ? "❤️" : "🤝"}
      </div>

      {/* Free entries carry no photos, so both avatars are the fallback — the
          card still reads as a profile card rather than a bare name. */}
      <div className="ll-card-names">
        <Person name={item.name1} />

        <span
          className="ll-connector"
          aria-hidden="true"
        >
          ❤️
        </span>

        <Person name={item.name2} />
      </div>

      <div
        className={`ll-type-pill ${
          type === "love"
            ? "ll-pill-love"
            : "ll-pill-friend"
        }`}
      >
        {type === "love"
          ? "❤️ Love Story"
          : "🤝 Friendship"}
      </div>

      {item.message && (
        <div className="ll-card-message-wrap">
          <p className="ll-card-message">
            {item.message}
          </p>
        </div>
      )}

      <TimerBar expiresAt={expiresAt} />
    </article>
  );
});

/* =========================================================
   PAID CARD
========================================================= */

const PaidCard = memo(function PaidCard({
  item,
  index,
}) {
  const isLove = item.type === "love";
  const partner = (item.partnerName || "").trim();

  const expiresAt = useMemo(() => {
    const value = new Date(item.expiresAt).getTime();

    return Number.isFinite(value) ? value : 0;
  }, [item.expiresAt]);

  return (
    <article
      className={[
        "ll-card",
        index === 0 ? "ll-card-gold" : "",
        index === 1 ? "ll-card-silver" : "",
        index === 2 ? "ll-card-bronze" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <RankBadge index={index} />

      <div className="ll-card-heart" aria-hidden="true">
        {isLove ? "❤️" : "🤝"}
      </div>

      {/* Each photo is read from ITS OWN field on this entry — profileImage
          belongs to `name`, partnerImage to `partnerName` — so a face can
          never appear under the wrong person's name. Either may be absent;
          LoveAvatar then draws that person's initial. */}
      <div className={`ll-card-names ${partner ? "" : "ll-names-solo"}`}>
        <Person name={item.name} image={item.profileImage} />

        {partner && (
          <>
            <span
              className="ll-connector"
              aria-hidden="true"
            >
              ❤️
            </span>

            <Person name={partner} image={item.partnerImage} />
          </>
        )}
      </div>

      {item.city && (
        <div className="ll-card-city">
          <span aria-hidden="true">📍</span>
          {/* City only — one main city per state, so "Bengaluru" already says
              Karnataka. */}
          <span>{item.city}</span>
        </div>
      )}

      <div
        className={`ll-type-pill ${
          isLove
            ? "ll-pill-love"
            : "ll-pill-friend"
        }`}
      >
        {isLove
          ? "❤️ Love Story"
          : "🤝 Friendship"}
      </div>

      {item.content && (
        <div className="ll-card-message-wrap">
          <p className="ll-card-message">
            {item.content}
          </p>
        </div>
      )}

      <TimerBar expiresAt={expiresAt} />
    </article>
  );
});

/* =========================================================
   BACKGROUND
========================================================= */

const BackgroundEffects = memo(function BackgroundEffects() {
  return (
    <>
      <div className="ll-sky-show">
        <div className="ll-moon">🌙</div>
        <div className="ll-sun">☀️</div>
        <div className="ll-rainbow" />

        <div className="ll-stars">
          <span className="ll-star s1">✦</span>
          <span className="ll-star s2">✦</span>
          <span className="ll-star s3">✦</span>
          <span className="ll-star s4">✦</span>
          <span className="ll-star s5">✦</span>
          <span className="ll-star s6">✦</span>
          <span className="ll-star s7">✦</span>
          <span className="ll-star s8">✦</span>
        </div>
      </div>

      <div className="ll-waterfall">
        <span className="ll-water-drop d1" />
        <span className="ll-water-drop d2" />
        <span className="ll-water-drop d3" />
        <span className="ll-water-drop d4" />
        <span className="ll-water-drop d5" />
        <span className="ll-water-drop d6" />
      </div>

      <div className="ll-petals-layer">
        <span className="ll-petal p1">🌹</span>
        <span className="ll-petal p2">🌹</span>
        <span className="ll-petal p3">🌹</span>
        <span className="ll-petal p4">🌹</span>
        <span className="ll-petal p5">🌹</span>
        <span className="ll-petal p6">🌹</span>

        <span className="ll-petal h1">❤️</span>
        <span className="ll-petal h2">❤️</span>
        <span className="ll-petal h3">❤️</span>

        <span className="ll-petal e1">✨</span>
        <span className="ll-petal e2">✨</span>
      </div>

      <div className="ll-watershow">
        <div className="ll-fountain-glow" />
      </div>
    </>
  );
});

/* =========================================================
   MAIN PAGE
========================================================= */

export default function LoveListPage() {
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [paidEntries, setPaidEntries] = useState([]);
  const [myEntry, setMyEntry] = useState(null);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [closingCard, setClosingCard] = useState(false);

  /* =======================================================
     FETCH DATA
  ======================================================= */

  const fetchData = useCallback(
    async (showLoader = false) => {
      try {
        if (showLoader) {
          setRefreshing(true);
        }

        const response = await api.get(LOVE_URL);

        const list = Array.isArray(response.data)
          ? response.data
          : [];

        const now = Date.now();

        const valid = list
          .filter((item) => {
            if (!item?.premiumAt) return false;

            const start = new Date(
              item.premiumAt
            ).getTime();

            return (
              Number.isFinite(start) &&
              now - start >= 0 &&
              now - start < DAY_MS
            );
          })
          .sort(
            (a, b) =>
              Number(b.amount || 0) -
              Number(a.amount || 0)
          );

        setData(valid);
      } catch (error) {
        console.error(
          "Love list fetch error:",
          error?.message || error
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    []
  );

  /* =======================================================
     FETCH PAID
  ======================================================= */

  const fetchPaid = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");

      const requests = [
        api.get(PAID_URL),
      ];

      if (token) {
        requests.push(
          api.get(`${PAID_URL}/mine`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          })
        );
      }

      const results = await Promise.allSettled(
        requests
      );

      /* Public paid entries */

      const publicResult = results[0];

      if (
        publicResult?.status === "fulfilled"
      ) {
        const list = Array.isArray(
          publicResult.value.data
        )
          ? publicResult.value.data
          : [];

        const now = Date.now();

        const valid = list.filter((item) => {
          const expiry = new Date(
            item?.expiresAt
          ).getTime();

          return (
            Number.isFinite(expiry) &&
            expiry > now
          );
        });

        setPaidEntries(valid);
      }

      /* My entry */

      if (
        token &&
        results[1]?.status === "fulfilled"
      ) {
        const mine = Array.isArray(
          results[1].value.data
        )
          ? results[1].value.data
          : [];

        const openEntry =
          mine.find(
            (entry) => !entry.cardClosed
          ) || null;

        setMyEntry(openEntry);
      }
    } catch (error) {
      console.error(
        "Paid entries fetch error:",
        error?.message || error
      );
    }
  }, []);

  /* =======================================================
     INITIAL LOAD
  ======================================================= */

  useEffect(() => {
    fetchData(true);
    fetchPaid();
  }, [fetchData, fetchPaid]);

  /* =======================================================
     REFRESH EVERY 60 SEC
     API calls run in parallel.
  ======================================================= */

  useEffect(() => {
    const refresh = async () => {
      await Promise.allSettled([
        fetchData(false),
        fetchPaid(),
      ]);
    };

    const id = setInterval(
      refresh,
      REFRESH_MS
    );

    return () => {
      clearInterval(id);
    };
  }, [fetchData, fetchPaid]);

  /* =======================================================
     CLOSE CARD
  ======================================================= */

  const handleCloseCard = useCallback(async () => {
    if (!myEntry?._id) return;

    const token =
      localStorage.getItem("token");

    if (!token) {
      setMyEntry(null);
      return;
    }

    setClosingCard(true);

    try {
      await api.patch(
        `${PAID_URL}/${myEntry._id}/close`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setMyEntry(null);
    } catch (error) {
      console.error(
        "Close card error:",
        error?.message || error
      );
    } finally {
      setClosingCard(false);
    }
  }, [myEntry]);

  /* =======================================================
     MEMOIZED GRID
  ======================================================= */

  const paidGrid = useMemo(() => {
    if (!paidEntries.length) return null;

    return (
      <div className="ll-cards-grid">
        {paidEntries.map((item, index) => (
          <PaidCard
            key={item._id || `paid-${index}`}
            item={item}
            index={index}
          />
        ))}
      </div>
    );
  }, [paidEntries]);

  const normalGrid = useMemo(() => {
    if (!data.length) return null;

    return (
      <div className="ll-cards-grid">
        {data.map((item, index) => (
          <LoveCard
            key={item._id || `love-${index}`}
            item={item}
            index={index}
          />
        ))}
      </div>
    );
  }, [data]);

  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <div className="ll-wrapper">
      <BackgroundEffects />

      <Navbar />

      <main className="ll-page">
        <BannerAds />

        <section className="ll-hero">
          <div className="ll-hero-aura" />

          <div className="ll-hero-badge">
            <span className="ll-hero-dot" />

            ❤️ Live Leaderboard

            <span className="ll-hero-dot" />
          </div>

          <h1 className="ll-hero-title">
            Top Lovers & Friendships
          </h1>

          <p className="ll-hero-tagline">
            Where love stories are celebrated forever 💌
          </p>

          <p className="ll-hero-sub">
            Want your love or friend's name featured here?
          </p>

          <button
            type="button"
            className="ll-cta-link"
            onClick={() => navigate("/loveadd")}
          >
            ✍️ Add Your Name — ₹12 for 24 hours
          </button>

          {/* ── CITY PRIDE CTA ──
              Opens the Live City Leaderboard. Declared alongside the existing
              CTA, which is unchanged. */}
          <div className="ll-city-cta">
            <button
              type="button"
              className="ll-city-cta-btn"
              onClick={() => navigate("/city-leaderboard")}
            >
              🏆 TAKE YOUR CITY FIRST
            </button>

            <p className="ll-city-cta-sub">Let's make our city #1.</p>
          </div>

          {refreshing && (
            <div className="ll-refresh-status">
              Updating…
            </div>
          )}
        </section>

        {myEntry && (
          <LovePaidCard
            entry={myEntry}
            onClose={handleCloseCard}
            closing={closingCard}
          />
        )}

        {loading ? (
          <div className="ll-loading">
            <div className="ll-loading-heart">
              ❤️
            </div>

            <p>Loading love stories...</p>
          </div>
        ) : (
          <>
            {paidGrid}

            {data.length === 0 &&
            paidEntries.length === 0 ? (
              <div className="ll-empty">
                <div className="ll-empty-icon">
                  💌
                </div>

                <h3 className="ll-empty-title">
                  No entries yet
                </h3>

                <p>
                  Be the first to celebrate your love!
                </p>
              </div>
            ) : (
              normalGrid
            )}
          </>
        )}
      </main>
    </div>
  );
}